/**
 * Acquisition → Performance Overview figure mapping (pure).
 *
 * The KPI grid is harmonised with the Home / Stats range selector (same
 * `HomeInterval` options + window logic). Each tile shows the metric over TWO
 * windows:
 *  - LEFT  = the selected range (e.g. Today, Last 30 days);
 *  - RIGHT = the "next step up" range (e.g. Today → This week; Last 30 days → Last
 *            60 days), per {@link RIGHT_STEP}.
 * Both columns carry a period-over-period delta vs the equal window before them (the
 * API has no comparison field). Changing the selector re-keys BOTH columns.
 *
 * Kept free of Redux/effects (no store imports) so it can be unit tested directly;
 * the effectful fetching + window maths live in {@link useAcquisitionPerformance}.
 * Mirrors Home's `stats-mapping.ts` / `useHomeStats.ts` split.
 */
import type { HomeInterval, HomePeriod, Kpi, KpiStat } from '../../mock-data/types';
import { AMOUNT_SCALE, HOME_CHANNELS, HOME_INTERVAL_API, customDays, isHomeInterval } from '../home/stats-mapping';
import { type Bucket, deltaPct, sumBuckets } from '../home/stats-shape';

/**
 * The acquisition-channel `aft` literals to sum for the Performance Overview totals,
 * i.e. the SAME six channels the Home Acquisition Channels grid shows (web, app,
 * crypto, video, affiliates, influencers). The figures come from
 * `daily_affiliate_activity` and we sum only these groups, so the total counts only
 * marketing-acquisition records, excluding the empty `aft` and the non-marketing
 * `direct` / `organic` groups (which `daily_client_activity` would otherwise include).
 */
export const ACQUISITION_AFTS: readonly string[] = HOME_CHANNELS.map((channel) => channel.aft);

/** The right column's window for each selected (left) range, the "next step up". */
export type RightStep = {
  /** Window length in days (a rolling window ending today, end-exclusive). */
  spanDays: number;
  /** History bucket size, coarser for longer windows (the figure sums every bucket). */
  intervalSlice: string;
};

/**
 * "Next step up" ladder (user-defined): Today/Yesterday → this week; Last 7 days →
 * last 30 days; Last 30 days → last 60 days; Last 90 days → last 6 months; Last 6
 * months → last year; Last year → last 2 years. The right window always includes
 * today (a rolling window), so it stays live. Labels/captions are translated at the
 * render site (see `index.tsx`); this is windows only.
 */
export const RIGHT_STEP: Record<HomeInterval, RightStep> = {
  TODAY: { spanDays: 7, intervalSlice: '1 day' },
  YESTERDAY: { spanDays: 7, intervalSlice: '1 day' },
  '7D': { spanDays: 30, intervalSlice: '1 day' },
  '30D': { spanDays: 60, intervalSlice: '1 day' },
  '90D': { spanDays: 182, intervalSlice: '1 week' },
  '6M': { spanDays: 365, intervalSlice: '1 week' },
  '1Y': { spanDays: 730, intervalSlice: '1 month' },
};

/** The presets a custom range borrows its step from, shortest first. */
const STEP_LADDER: readonly HomeInterval[] = ['7D', '30D', '90D', '6M', '1Y'];

/**
 * The preset whose "next step up" a period's right column uses: a preset's own; for a custom range, the shortest
 * preset at least as long (a 20-day range steps up like Last 30 days, to the last 60 days), a year at most. Pure.
 */
export const stepPresetOf = (period: HomePeriod): HomeInterval => {
  if (isHomeInterval(period)) return period;
  const days = customDays(period)?.days ?? 7;
  return STEP_LADDER.find((preset) => HOME_INTERVAL_API[preset].spanDays >= days) ?? '1Y';
};

/** A period's right-column window (see {@link stepPresetOf}). Pure. */
export const rightStepOf = (period: HomePeriod): RightStep => RIGHT_STEP[stepPresetOf(period)];

/* ---------- rolling window for the right column ----------
 * Local calendar dates (YYYY-MM-DD), end-exclusive, ending tomorrow 00:00 local so
 * the window includes today, identical to Home's multi-day window formula, so the
 * right figures line up with the equivalent Home range. */
const pad = (n: number): string => String(n).padStart(2, '0');
const localDateYMD = (nowMs: number, dayOffset: number): string => {
  const d = new Date(nowMs);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + dayOffset);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export type Window = { startDate: string; endDate: string };

/** The rolling `spanDays`-day window ending today (end-exclusive). */
export const rollingWindow = (spanDays: number, nowMs: number): Window => ({
  startDate: localDateYMD(nowMs, 1 - spanDays),
  endDate: localDateYMD(nowMs, 1),
});

/**
 * Per-KPI source, keyed by the mock KPI `id`. Money sums/ratios scale to whole
 * currency (`AMOUNT_SCALE`, 1e8); counts plot as-is.
 *
 *  - `sum`  , a window total of `fields` (casino + sport for money totals).
 *  - `ratio`, a per-FTD figure: `sum(num) / sum(den)` (the delta compares the ratios).
 *  - `missing`, no API field exists (the ad-spend KPIs) → render `?`.
 *
 * The derived formulas are confirmed against the old mock figures (1188/22 ≈ $54 for
 * Avg Deposit/FTD, 4200/22 ≈ $191 for Turnover/FTD). The three ad-spend KPIs need a
 * marketing-spend metric no stats data set exposes, see `missing-data.md`; add their
 * `sum`/`ratio` specs here and they go live unchanged.
 */
export type AcqKpiSpec =
  | { kind: 'sum'; fields: readonly string[]; money: boolean }
  | { kind: 'ratio'; num: readonly string[]; den: readonly string[]; money: boolean }
  | { kind: 'missing' };

export const PERFORMANCE_KPI_SPECS: Record<string, AcqKpiSpec> = {
  // Ad-spend dependent, no marketing-spend field in any stats data set.
  'blended-cpa': { kind: 'missing' },
  'budget-spent': { kind: 'missing' },
  roi: { kind: 'missing' },
  // Sourced from daily_affiliate_activity, summed over the acquisition-channel afts.
  registrations: { kind: 'sum', fields: ['registrations'], money: false },
  ftds: { kind: 'sum', fields: ['ftds'], money: false },
  'ftd-deposits': { kind: 'sum', fields: ['ftd_total_amount'], money: true },
  'avg-deposit-ftd': { kind: 'ratio', num: ['ftd_total_amount'], den: ['ftds'], money: true },
  'total-turnover': { kind: 'sum', fields: ['casino_bet_amount', 'sport_bet_amount'], money: true },
  'turnover-ftd': { kind: 'ratio', num: ['casino_bet_amount', 'sport_bet_amount'], den: ['ftds'], money: true },
  ggr: { kind: 'sum', fields: ['casino_ggr', 'sport_ggr'], money: true },
};

/**
 * The display string for a built stat: the marker glyph for the missing / failing /
 * indeterminate states (`?` / `N/A` / `–`), else the value. The single convention the
 * acquisition root, the channel metric grid and the channel campaign tiles all render
 * through, so a "no data" figure looks identical everywhere.
 */
export const markerText = (stat: Pick<KpiStat, 'value' | 'missing' | 'error' | 'indeterminate'>): string =>
  stat.missing ? '?' : stat.error ? 'N/A' : stat.indeterminate ? '–' : stat.value;

const safeRatio = (num: number, den: number): number => (den === 0 ? 0 : num / den);

/** Whole-currency / whole-count display string, grouped (matches the Home figures). */
const formatValue = (value: number, money: boolean): string =>
  `${money ? '$' : ''}${Math.round(value).toLocaleString('en-US')}`;

/** A signed `±N%` change string + its increase/decrease tone, from a delta percent. A
 *  zero delta is neither: it is flagged `flat` and drops the sign, because a figure that
 *  held steady is not a rise of zero and should not read as one. */
const formatChange = (delta: number): Pick<KpiStat, 'change' | 'changeType' | 'flat'> =>
  delta === 0
    ? { change: '0%', changeType: 'increase', flat: true }
    : { change: `${delta > 0 ? '+' : ''}${delta}%`, changeType: delta < 0 ? 'decrease' : 'increase' };

/** One figure from its spec + the current / baseline buckets, the shared stat
 *  shape ({@link KpiStat}) reused by the channel detail metrics too. */
export const buildStat = (spec: AcqKpiSpec, current: Bucket[], baseline: Bucket[], errored: boolean): KpiStat => {
  if (spec.kind === 'missing') {
    return { value: '', change: '', changeType: 'increase', missing: true };
  }
  if (errored) {
    // The request is failing and nothing is cached: render `N/A` instead of a fake 0.
    return { value: '', change: '', changeType: 'increase', error: true };
  }
  const scale = spec.money ? 1 / AMOUNT_SCALE : 1;
  if (spec.kind === 'sum') {
    const total = sumBuckets(current, spec.fields);
    return {
      value: formatValue(total * scale, spec.money),
      ...formatChange(deltaPct(total, sumBuckets(baseline, spec.fields))),
    };
  }
  // ratio: the delta compares the period ratios (scale cancels, so it runs on raw sums).
  const currentDen = sumBuckets(current, spec.den);
  if (currentDen === 0) {
    // Divide by zero (e.g. a per-FTD figure with zero FTDs): the value is undefined,
    // render `–` (indeterminate) rather than a misleading $0.
    return { value: '', change: '', changeType: 'increase', indeterminate: true };
  }
  const currentRatio = safeRatio(sumBuckets(current, spec.num), currentDen);
  const baselineRatio = safeRatio(sumBuckets(baseline, spec.num), sumBuckets(baseline, spec.den));
  return {
    value: formatValue(currentRatio * scale, spec.money),
    ...formatChange(deltaPct(currentRatio, baselineRatio)),
  };
};

/** One window's buckets + whether its request hard-errored (nothing cached). */
export type ColumnInput = { current: Bucket[]; baseline: Bucket[]; errored: boolean };

/**
 * Override each mock KPI's `today` (left = selected range) and `month` (right = step
 * up) with the API-sourced figures, leaving the static title/subtitle untouched. A
 * KPI `id` with no spec keeps its mock columns. Pure (no hooks) so it can be
 * unit-tested directly.
 */
export const buildPerformanceKpis = (mockKpis: Kpi[], left: ColumnInput, right: ColumnInput): Kpi[] =>
  mockKpis.map((kpi) => {
    const spec = PERFORMANCE_KPI_SPECS[kpi.id];
    if (!spec) return kpi;
    return {
      ...kpi,
      today: buildStat(spec, left.current, left.baseline, left.errored),
      month: buildStat(spec, right.current, right.baseline, right.errored),
    };
  });

/* ---------- ad-spend KPIs (sourced from the Casino Ad Service) ----------
 * The three KPIs the stats data sets cannot supply (they have no marketing-spend
 * field), now sourced from the ad-service campaign performance `cost`. Their specs
 * above stay `{ kind: 'missing' }` so `buildPerformanceKpis` keeps rendering `?`
 * until the ad-spend cost loads; the effectful layer (`useAcquisitionPerformance`)
 * then OVERRIDES these three columns with {@link buildAdSpendStat}. Keeping this
 * additive leaves `buildStat` / `buildPerformanceKpis` (and their tests) untouched. */

/** The three KPI ids backed by the ad-service spend, not the stats data sets. */
export const AD_SPEND_KPI_IDS = ['budget-spent', 'blended-cpa', 'roi'] as const;
export type AdSpendKpiId = (typeof AD_SPEND_KPI_IDS)[number];

/** True for a KPI id the ad-service spend backs (a narrowing guard). */
export const isAdSpendKpiId = (id: string): id is AdSpendKpiId =>
  (AD_SPEND_KPI_IDS as readonly string[]).includes(id);

/**
 * One ad-spend KPI column: the window's marketing spend (summed ad-service campaign
 * `cost`, whole currency) and its prior-window baseline, plus the SAME stats buckets
 * the other KPIs use (for the FTDs / GGR that CPA and ROI divide by). `cost` is
 * `null` until the spend request resolves; `errored` when it failed.
 */
export type AdSpendColumnInput = {
  cost: number | null;
  baselineCost: number | null;
  current: Bucket[];
  baseline: Bucket[];
  errored: boolean;
};

/** Sum casino + sport GGR, scaled to whole currency (matches the GGR KPI). */
const ggrWhole = (buckets: Bucket[]): number => sumBuckets(buckets, ['casino_ggr', 'sport_ggr']) / AMOUNT_SCALE;

/**
 * Build one ad-spend KPI column from the spend cost + stats buckets:
 *  - `budget-spent`: the spend itself (money), delta vs the baseline spend.
 *  - `blended-cpa` : `cost / FTDs` (money); zero FTDs -> `–` (indeterminate).
 *  - `roi`         : `(GGR - cost) / cost` as a percent; zero cost -> `–`.
 * `errored` -> `N/A`; not-yet-loaded (`cost == null`) -> `?` (keeps the pre-wire
 * look until the spend lands). Pure, so it unit-tests directly like {@link buildStat}.
 *
 * NOTE: ROI / CPA mix the ad-service spend currency with the stats GGR currency;
 * this assumes they match (true for single-currency brands). Convert when a brand's
 * ad spend and reporting currency differ (no multi-currency data to confirm yet).
 */
export const buildAdSpendStat = (id: AdSpendKpiId, col: AdSpendColumnInput): KpiStat => {
  if (col.errored) {
    return { value: '', change: '', changeType: 'increase', error: true };
  }
  if (col.cost === null || col.baselineCost === null) {
    return { value: '', change: '', changeType: 'increase', missing: true };
  }
  if (id === 'budget-spent') {
    return { value: formatValue(col.cost, true), ...formatChange(deltaPct(col.cost, col.baselineCost)) };
  }
  if (id === 'blended-cpa') {
    const ftds = sumBuckets(col.current, ['ftds']);
    if (ftds === 0) {
      return { value: '', change: '', changeType: 'increase', indeterminate: true };
    }
    const cpa = col.cost / ftds;
    const baseCpa = safeRatio(col.baselineCost, sumBuckets(col.baseline, ['ftds']));
    return { value: formatValue(cpa, true), ...formatChange(deltaPct(cpa, baseCpa)) };
  }
  // roi
  if (col.cost === 0) {
    return { value: '', change: '', changeType: 'increase', indeterminate: true };
  }
  const roi = ((ggrWhole(col.current) - col.cost) / col.cost) * 100;
  const baseRoi =
    col.baselineCost === 0 ? 0 : ((ggrWhole(col.baseline) - col.baselineCost) / col.baselineCost) * 100;
  return { value: `${Math.round(roi)}%`, ...formatChange(deltaPct(roi, baseRoi)) };
};
